import React from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Link,
  useTheme,
} from '@mui/material';
import { DeleteOutline, Download, Security, Login, Gavel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ResponsiveContainer from '../layouts/ResponsiveContainer';
import AppBarContent from '../layouts/AppBarContent';
import { font } from '../../utils/fontHelper';

/**
 * Public data-deletion information page (e.g. store listings, logged-out visitors).
 * Authenticated users use /data-deletion with the full in-app data manager.
 */
const DataDeletionPublicPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
        pb: 6,
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: isDark ? 'rgba(28, 28, 30, 0.75)' : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(16px)',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 'var(--safe-area-inset-top)',
        }}
      >
        <Toolbar disableGutters sx={{ alignItems: 'center' }}>
          <AppBarContent sx={{ justifyContent: 'space-between' }}>
          <IconButton
            edge="start"
            onClick={() => navigate('/')}
            aria-label="Back to home"
            sx={{ color: 'text.primary' }}
          >
            <ArrowLeftIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontFamily: font(), flex: 1, fontSize: 17 }}
          >
            Data & account deletion
          </Typography>
          </AppBarContent>
        </Toolbar>
      </AppBar>

      <ResponsiveContainer maxWidth={720}>
        <Box
          sx={{
            pt: { xs: 'calc(var(--safe-area-inset-top) + 88px)', md: 'calc(var(--safe-area-inset-top) + 96px)' },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              p: { xs: 3, sm: 4 },
              mb: 4,
              background: isDark
                ? `linear-gradient(145deg, ${theme.palette.primary.dark}33 0%, ${theme.palette.background.paper} 100%)`
                : `linear-gradient(145deg, ${theme.palette.primary.light}44 0%, ${theme.palette.background.paper} 100%)`,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                fontFamily: font(),
                mb: 1.5,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              Your data, your control
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
              Watchlist Crew lets you export or delete the personal data associated with your account. This page
              describes what we process and how to request deletion in the app or on the web.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Login />}
              onClick={() => navigate('/')}
              sx={{ mt: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Sign in to manage my data
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1.5, color: 'text.secondary' }}>
              After signing in, open Profile → Settings → My Data, or stay on this URL — it becomes your full data
              dashboard.
            </Typography>
          </Box>

          <Stack spacing={2.5} sx={{ mb: 4 }}>
            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Security color="primary" sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: font(), mb: 1 }}>
                      Categories of data we process
                    </Typography>
                    <Typography component="ul" variant="body2" sx={{ color: 'text.secondary', pl: 2, m: 0, lineHeight: 1.8 }}>
                      <li>Account identifiers (such as user ID and sign-in email)</li>
                      <li>Profile information you choose to share (display name, photo, bio)</li>
                      <li>Movie-related data: watchlists, watched titles, ratings, and reviews</li>
                      <li>Social and activity data (for example follows, likes, notifications, crew activity)</li>
                      <li>App preferences (theme, language, content visibility settings)</li>
                      <li>Technical data needed to run the service (see our Privacy Policy)</li>
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Download color="primary" sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: font(), mb: 1 }}>
                      Export a copy
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      Signed-in users can download a JSON summary of their data from{' '}
                      <strong>Settings → My Data → Download My Data</strong>. The file includes profile basics,
                      preferences, and movie-related entries available in the app at export time.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <DeleteOutline color="error" sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: font(), mb: 1 }}>
                      Delete your data (keep your account)
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 2 }}>
                      From <strong>Settings → My Data → Delete All My Data</strong>, you can remove your movie data,
                      preferences, and related app content from our systems. This does not delete your login account by
                      itself. Processing usually completes within a few minutes; you may need to sign out and back in on
                      some devices to refresh cached content.
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      <strong>Delete account:</strong> Use <strong>Delete My Account</strong> on the same screen to
                      remove your account and associated data as described in the confirmation flow.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Gavel color="action" sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: font(), mb: 1 }}>
                      Legal & contact
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1.5 }}>
                      For more detail on how we handle personal data, read our policies:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={2}>
                      <Link href="/privacy.html" target="_blank" rel="noopener noreferrer" underline="hover">
                        Privacy Policy
                      </Link>
                      <Link href="/terms.html" target="_blank" rel="noopener noreferrer" underline="hover">
                        Terms & Conditions
                      </Link>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mt: 2 }}>
                      Questions or requests: use <strong>Feedback</strong> in Settings so we can reply in context with
                      your account.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>

          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center' }}>
            Watchlist Crew — {typeof window !== 'undefined' ? window.location.host : ''}
          </Typography>
        </Box>
      </ResponsiveContainer>
    </Box>
  );
};

export default DataDeletionPublicPage;
